import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hhmm, time, todayIE } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LogIn, LogOut, Coffee, MapPin, ShieldCheck, ShieldAlert, Plus, Loader2, Play, Pause, Square, AlertTriangle, Camera } from "lucide-react";
import { JobInfo } from "@/components/JobInfo";
import MyTimeEntries from "@/components/MyTimeEntries";

const ADMIN_WORK_ROLES = ["supervisor", "office", "management", "support"];
const BUSINESS = "__business";

function TimerNote({ timer }) {
  const [note, setNote] = useState(timer.note || "");
  const [saved, setSaved] = useState(timer.note || "");
  useEffect(() => { setNote(timer.note || ""); setSaved(timer.note || ""); }, [timer.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    if (note.trim() === saved.trim()) return;
    try { await api.post("/job-timer/note", { note }); setSaved(note); toast.success("Note saved to this job"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-3" data-testid="timer-note-box">
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} onBlur={save} rows={2} placeholder="Add a note about this job (saved with your time)" data-testid="timer-note" />
      <div className="flex justify-between items-center mt-1">
        <span className="text-[11px] text-muted-foreground">{note.trim() !== saved.trim() ? "Not saved yet" : saved ? "Saved — booked with your time when you finish" : ""}</span>
        {note.trim() !== saved.trim() && <Button size="sm" variant="outline" onClick={save} data-testid="timer-note-save">Save note</Button>}
      </div>
    </div>
  );
}

function PhotoButton({ jobId }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("kind", "photo");
        await api.post(`/jobs/${jobId}/attachments/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added to the job`);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <label className={`kiosk-btn mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-border bg-accent hover:bg-accent/70 cursor-pointer font-bold transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="timer-add-photo">
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}{busy ? "Uploading…" : "Add Photo"}
      <input type="file" accept="image/*" capture="environment" multiple onChange={upload} className="hidden" data-testid="timer-photo-input" />
    </label>
  );
}

function JobPeek({ jobId, testid }) {
  const [job, setJob] = useState(null);
  useEffect(() => { if (jobId) api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setJob(null)); else setJob(null); }, [jobId]);
  if (!job) return null;
  return <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid={testid}><JobInfo job={job} big /></div>;
}

function MyHours({ data }) {
  if (!data) return null;
  const cell = (label, m, testid, cls) => (
    <div className="text-center"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-heading text-xl font-extrabold font-mono-num ${cls}`} data-testid={testid}>{hhmm(m)}</div></div>
  );
  const row = (title, d, p) => (
    <div className="grid grid-cols-4 items-center gap-2 py-2">
      <div className="text-xs font-bold uppercase">{title}</div>
      {cell("Clocked", d.clock_minutes, `${p}-clock`, "text-sky-300")}
      {cell("Projects", d.project_minutes, `${p}-project`, "text-primary")}
      {cell("Unalloc.", Math.max(d.clock_minutes - d.project_minutes, 0), `${p}-unallocated`, "text-amber-400")}
    </div>
  );
  return (
    <div className="card-industrial p-5 mb-5" data-testid="my-hours">
      <h3 className="font-heading font-bold uppercase tracking-wide text-sm mb-1">My Hours</h3>
      <p className="text-[11px] text-muted-foreground mb-1">Clock in/out hours and project hours are tracked separately.</p>
      <div className="divide-y divide-border">{row("Today", data.today, "hours-today")}{row("This week", data.week, "hours-week")}</div>
    </div>
  );
}

export default function Clock() {
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [today, setToday] = useState([]);
  const [locations, setLocations] = useState([]);

  // booking form
  const [jobId, setJobId] = useState("");
  const [custId, setCustId] = useState("");
  const business = custId === BUSINESS;
  const adminWork = ADMIN_WORK_ROLES.includes(user.role);
  const [stageName, setStageName] = useState("");
  const [stages, setStages] = useState([]);
  const [mins, setMins] = useState(60);
  const [exact, setExact] = useState(false);
  const [times, setTimes] = useState({ start: "", finish: "" });
  const exactMins = (() => {
    if (!times.start || !times.finish) return null;
    const [a, b] = times.start.split(":").map(Number); const [c, d] = times.finish.split(":").map(Number);
    return c * 60 + d - (a * 60 + b);
  })();
  const exactBad = exact && exactMins !== null && exactMins <= 0;
  const dur = exact ? Math.max(exactMins || 0, 0) : mins;
  const [note, setNote] = useState("");

  const todayStr = todayIE();

  // ---- project timer ----
  const [timer, setTimer] = useState(null);
  const [, setTick] = useState(0);
  const baseRef = useRef({ secs: 0, at: Date.now(), running: false });
  const applyTimer = (data) => {
    setTimer(data || null);
    if (data) baseRef.current = { secs: data.elapsed_seconds || 0, at: Date.now(), running: data.status === "running" };
  };
  const loadTimer = useCallback(async () => { try { const { data } = await api.get("/job-timer/active"); applyTimer(data); } catch (e) {} }, []);
  useEffect(() => { loadTimer(); }, [loadTimer]);
  const [myHours, setMyHours] = useState(null);
  const [remindH, setRemindH] = useState(4);
  useEffect(() => { api.get("/settings").then((r) => setRemindH(Number(r.data.timer_reminder_hours) || 4)).catch(() => {}); }, []);
  useEffect(() => { const i = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(i); }, []);
  const timerSecs = () => { const b = baseRef.current; return Math.floor(b.secs + (b.running ? (Date.now() - b.at) / 1000 : 0)); };
  const hms = (s) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const startTimer = async () => {
    if (!jobId && !business) return toast.error("Select a job below first");
    if (!stageName) return toast.error("Select a task below first");
    try { const { data } = await api.post("/job-timer/start", { job_id: business ? null : jobId, stage_name: stageName }); applyTimer(data); toast.success("Timer started"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const pauseResume = async () => {
    try { const { data } = await api.post(timer.status === "running" ? "/job-timer/pause" : "/job-timer/resume"); applyTimer(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const finishTimer = async () => {
    try { const { data } = await api.post("/job-timer/stop", {}); setTimer(null); toast.success(`Booked ${hhmm(data.booked_minutes)} to ${data.job_number}`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const refresh = useCallback(async () => {
    const [a, j, t, l, h] = await Promise.all([
      api.get("/attendance/active"),
      api.get("/jobs", { params: { active: true } }),
      api.get("/job-time", { params: { date: todayStr } }),
      api.get("/locations"),
      api.get("/my-hours"),
    ]);
    setMyHours(h.data);
    setShift(a.data || null);
    setJobs(j.data || []);
    setToday(t.data || []);
    setLocations(l.data || []);
    setLoading(false);
  }, [todayStr]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!jobId) { setStages([]); setStageName(business ? "Admin Work" : ""); return; }
    setStageName("");
    api.get(`/jobs/${jobId}`).then((r) => setStages(r.data.stages || []));
  }, [jobId, business]);

  const getGps = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });

  const [clockLoc, setClockLoc] = useState(user.home_location || "Rathcairn");
  const [offNote, setOffNote] = useState("");
  const offSite = clockLoc === "Off Site";
  const clockIn = async () => {
    if (offSite && !offNote.trim()) { toast.error("Please add a note saying where you are working"); return; }
    setBusy(true);
    try {
      const gps = await getGps();
      const { data } = await api.post("/attendance/clock-in", { ...gps, location: clockLoc, ...(offSite ? { off_site_note: offNote.trim() } : {}) });
      setOffNote("");
      setShift(data);
      if (data.flagged) toast.warning(data.flag_reason || "Clock-in flagged for review");
      else toast.success(`Clocked in at ${data.location}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const act = async (path, ok) => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      setShift(path.includes("clock-out") ? null : data);
      toast.success(ok);
      if (data.timer_stopped) toast.info(`Job timer stopped — ${hhmm(data.timer_stopped.minutes)} booked to ${data.timer_stopped.job_number}`);
      if (data.timer_resumed) toast.info(`Back on ${data.timer_resumed.job_number} · ${data.timer_resumed.stage_name}`);
      loadTimer();
      api.get("/my-hours").then((r) => setMyHours(r.data));
      api.get("/job-time", { params: { date: todayStr } }).then((r) => setToday(r.data || []));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const bookTime = async () => {
    if (!jobId && !business) return toast.error("Select a job");
    if (!stageName) return toast.error("Select a task");
    if (exactBad) return toast.error("Finish time must be after the start time");
    setBusy(true);
    try {
      const body = exact ? { start_time: times.start, finish_time: times.finish } : { minutes: Number(mins) };
      await api.post("/job-time", { job_id: business ? null : jobId, stage_name: stageName, ...body, note, date: todayStr });
      toast.success(`Booked ${hhmm(dur)}`);
      setNote(""); setMins(60); setTimes({ start: "", finish: "" });
      api.get("/my-hours").then((r) => setMyHours(r.data));
      const t = await api.get("/job-time", { params: { date: todayStr } });
      setToday(t.data || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const bookedMins = today.reduce((s, r) => s + r.minutes, 0);
  const customers = Object.values(Object.fromEntries(jobs.filter((j) => j.customer_id).map((j) => [j.customer_id, { id: j.customer_id, name: j.customer_name }]))).sort((a, b) => a.name.localeCompare(b.name));
  const taskNames = [...new Set([...stages.map((s) => s.name), ...(adminWork ? ["Admin Work"] : [])])];
  const onBreak = !!shift?.breaks?.length && shift.breaks[shift.breaks.length - 1].end === null;

  if (loading) return <div className="p-6 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-md mx-auto p-4 pb-24">
      <div className="pt-2 pb-4">
        <h1 className="font-heading text-2xl font-extrabold uppercase">Hi, {user.name.split(" ")[0]}</h1>
        <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString("en-IE", { timeZone: "Europe/Dublin", weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      {/* Attendance kiosk */}
      <div className="card-industrial p-5 mb-5" data-testid="clock-widget">
        {shift ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400">On the clock</div>
                <div className="font-heading text-xl font-extrabold">Since {time(shift.clock_in_at)}</div>
              </div>
              <span className={`text-xs px-3 py-1.5 rounded-full font-mono flex items-center gap-1.5 border ${shift.geofence_ok ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-red-500/15 text-red-300 border-red-500/50"}`}>
                {shift.geofence_ok ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                {shift.geofence_ok ? "In range" : "Flagged"}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
              <MapPin className="w-4 h-4" /> {shift.location}{shift.off_site_note ? ` — ${shift.off_site_note}` : ""}
              {shift.distance_m != null && <span className="font-mono-num text-xs">· {shift.distance_m}m</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {onBreak ? (
                <Button onClick={() => act("/attendance/break-end", "Break ended")} disabled={busy}
                  data-testid="break-end-button" className="kiosk-btn bg-sky-500 hover:bg-sky-600 text-white col-span-2">
                  <Coffee className="w-5 h-5" /> End Break
                </Button>
              ) : (
                <Button onClick={() => act("/attendance/break-start", "On break")} disabled={busy}
                  data-testid="break-start-button" className="kiosk-btn bg-slate-800 hover:bg-slate-700 text-white border border-border">
                  <Coffee className="w-5 h-5" /> Break
                </Button>
              )}
              {!onBreak && (
                <Button onClick={() => act("/attendance/clock-out", "Clocked out")} disabled={busy}
                  data-testid="clock-out-button" className="kiosk-btn bg-red-600 hover:bg-red-700 text-white">
                  <LogOut className="w-5 h-5" /> Clock Out
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Not clocked in</div>
            <div className="text-left mb-3">
              <div className="text-xs font-bold uppercase mb-1.5">Where are you working?</div>
              <Select value={clockLoc} onValueChange={setClockLoc}>
                <SelectTrigger className="h-14 text-base" data-testid="clock-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.name} data-testid={`clock-loc-${l.name}`}>{l.name}</SelectItem>)}
                  <SelectItem value="Off Site" data-testid="clock-loc-off-site">Off Site</SelectItem>
                </SelectContent>
              </Select>
              {offSite && (
                <Textarea value={offNote} onChange={(e) => setOffNote(e.target.value)} rows={2} placeholder="Where are you working? e.g. Installing steel at customer site, Navan"
                  data-testid="clock-offsite-note" className="mt-2 text-base" />
              )}
            </div>
            <Button onClick={clockIn} disabled={busy || (offSite && !offNote.trim())} data-testid="clock-in-button"
              className="kiosk-btn bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> Clock In — {clockLoc}</>}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-3">{offSite ? "Off-site clock-ins record your GPS position with your note." : "GPS is checked against a 10m radius of the selected workshop."}</p>
          </div>
        )}
      </div>

      {/* Project timer */}
      <div className="card-industrial p-5 mb-5" data-testid="timer-widget">
        <h3 className="font-heading font-bold uppercase tracking-wide text-sm mb-3">Project Timer</h3>
        {timer ? (
          <>
            <div className="text-center py-2">
              <div className="font-mono-num text-5xl font-extrabold tracking-tight" data-testid="timer-display">{hms(timerSecs())}</div>
              <div className="text-sm text-muted-foreground mt-1" data-testid="timer-job-label">{timer.job_name || timer.job_number}{timer.stage_name ? ` · ${timer.stage_name}` : ""}</div>
              <div className={`text-[11px] font-mono uppercase tracking-widest mt-1 ${timer.status === "running" ? "text-emerald-400" : "text-amber-400"}`}>{timer.status}</div>
            </div>
            {timer.status === "running" && timerSecs() >= remindH * 3600 && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="timer-long-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Timer has been running over {remindH}h. Still working? If not, pause or finish it.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Button onClick={pauseResume} disabled={!shift || onBreak} data-testid="timer-pause" className="kiosk-btn bg-slate-800 hover:bg-slate-700 text-white border border-border">
                {timer.status === "running" ? <><Pause className="w-5 h-5" />Pause</> : <><Play className="w-5 h-5" />Resume</>}
              </Button>
              <Button onClick={finishTimer} data-testid="timer-finish" className="kiosk-btn bg-primary hover:bg-primary/90 text-primary-foreground"><Square className="w-5 h-5" />Finish</Button>
            </div>
            {timer.job_id && <PhotoButton jobId={timer.job_id} />}
            <TimerNote timer={timer} />
            <JobPeek jobId={timer.job_id} testid="timer-job-info" />
          </>
        ) : (
          <div className="text-center py-2">
            <p className="text-sm text-muted-foreground mb-3" data-testid="timer-hint">{!shift ? "Clock in first — you can't start a job until you're on the clock." : onBreak ? "You're on break. End your break to get back on a job." : "Select a job below, then start — the timer books the time automatically when you finish."}</p>
            <Button onClick={startTimer} disabled={(!jobId && !business) || !stageName || !shift || onBreak} data-testid="timer-start" className="kiosk-btn bg-emerald-600 hover:bg-emerald-700 text-white"><Play className="w-5 h-5" />Start Timer</Button>
          </div>
        )}
      </div>

      <MyHours data={myHours} />

      {/* Job time booking */}
      <div className="card-industrial p-5 mb-5">
        <h3 className="font-heading font-bold uppercase tracking-wide text-sm mb-3">Book Time to a Job</h3>
        <div className="space-y-3">
          <Select value={custId} onValueChange={(v) => { setCustId(v); setJobId(""); }}>
            <SelectTrigger data-testid="booking-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
            <SelectContent>
              {adminWork && <SelectItem value={BUSINESS}>Business (general admin)</SelectItem>}
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {business ? <p className="text-xs text-muted-foreground" data-testid="booking-business-note">Admin Work booked to the business, not to a job.</p> : (
          <Select value={jobId} onValueChange={setJobId} disabled={!custId}>
            <SelectTrigger data-testid="booking-job-select"><SelectValue placeholder={custId ? "Select job" : "Pick a customer first"} /></SelectTrigger>
            <SelectContent>
              {jobs.filter((j) => j.customer_id === custId).map((j) => <SelectItem key={j.id} value={j.id}>{j.job_name || j.job_number}{j.job_name && <span className="text-muted-foreground text-xs"> · {j.job_number}</span>}</SelectItem>)}
            </SelectContent>
          </Select>)}
          {(!timer || timer.job_id !== jobId) && <JobPeek jobId={jobId} testid="selected-job-info" />}
          {business && <div className="text-sm font-semibold" data-testid="booking-business-task">Task: Admin Work</div>}
          {jobId && (taskNames.length > 0 ? (
            <Select value={stageName} onValueChange={setStageName}>
              <SelectTrigger data-testid="booking-stage-select" className={stageName ? "" : "border-amber-500/60"}><SelectValue placeholder="Select task (required)" /></SelectTrigger>
              <SelectContent>
                {taskNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : <p className="text-xs text-red-400" data-testid="booking-no-tasks">This job has no tasks yet. Ask the office to add tasks.</p>)}
          <div>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-accent/60 mb-3" data-testid="booking-mode">
              {[["quick", "Quick add"], ["exact", "Exact times"]].map(([k, l]) => (
                <button key={k} onClick={() => setExact(k === "exact")} data-testid={`booking-mode-${k}`}
                  className={`py-2 rounded-md text-xs font-bold uppercase transition-colors ${(k === "exact") === exact ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Duration</span>
              <div className="flex items-center gap-3">
                <span className={`font-heading text-2xl font-extrabold font-mono-num ${exactBad ? "text-red-400" : ""}`} data-testid="booking-duration">{hhmm(dur)}</span>
                {!exact && <button onClick={() => setMins(0)} disabled={mins === 0} data-testid="booking-clear"
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold uppercase text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 transition-colors">Clear</button>}
              </div>
            </div>
            {exact ? (
              <div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs text-muted-foreground">Start<input type="time" value={times.start} onChange={(e) => setTimes({ ...times, start: e.target.value })} data-testid="booking-start-time"
                    className="mt-1 w-full h-12 rounded-md border border-input bg-background px-3 text-lg font-mono-num text-foreground" /></label>
                  <label className="text-xs text-muted-foreground">Finish<input type="time" value={times.finish} onChange={(e) => setTimes({ ...times, finish: e.target.value })} data-testid="booking-finish-time"
                    className="mt-1 w-full h-12 rounded-md border border-input bg-background px-3 text-lg font-mono-num text-foreground" /></label>
                </div>
                {exactBad && <p className="text-xs text-red-400 mt-2" data-testid="booking-time-error">Finish time must be after the start time. Please fix the times.</p>}
              </div>
            ) : (
            <div className="grid grid-cols-4 gap-2">
              {[15, 30, 60, 120].map((m) => (
                <button key={m} onClick={() => setMins((x) => x + m)} data-testid={`add-${m}`}
                  className="py-3 rounded-lg bg-accent hover:bg-accent/70 text-sm font-bold transition-colors">+{m >= 60 ? `${m / 60}h` : `${m}m`}</button>
              ))}
            </div>
            )}
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2} data-testid="booking-note" />
          {user.role === "shop_floor" && (!shift || onBreak) && <p className="text-xs text-amber-400" data-testid="booking-locked-hint">{!shift ? "Clock in first to book time to a job." : "End your break first to book time."}</p>}
          <Button onClick={bookTime} disabled={busy || (!jobId && !business) || !stageName || !dur || exactBad || (user.role === "shop_floor" && (!shift || onBreak))} data-testid="book-time-button"
            className="kiosk-btn bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="w-5 h-5" /> Book {hhmm(dur)}
          </Button>
        </div>
      </div>

      {/* Time entries with filters */}
      <MyTimeEntries customers={customers} refreshKey={today} />
    </div>
  );
}

